import ActionButton from '@/components/ActionButton';
import { getPageTitle, PageSEO } from '@/components/SEO';
import TextInput from '@/components/TextInput';
import siteMetadata from '@/data/siteMetadata';
import { FormEvent, useCallback, useMemo, useState } from 'react';
import { useMutate } from 'restful-react';

interface ContactFormBody {
  name: string;
  email: string;
  organization?: string;
  job_title?: string;
  phone_number?: string;
  details?: string;
}

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [details, setDetails] = useState('');

  const { mutate, loading, error } = useMutate<ContactFormBody>({
    path: '/api/contact',
    verb: 'POST',
  });

  const canSubmit = useMemo(() => {
    return name.length > 0 && email.length > 0;
  }, [name, email]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) return;

      const payload: ContactFormBody = {
        name,
        email,
        organization,
        job_title: jobTitle,
        phone_number: phoneNumber,
        details,
      };

      mutate(payload);
      setSubmitted(true);
    },
    [canSubmit, details, email, jobTitle, mutate, name, organization, phoneNumber]
  );

  const submitButtonText = useMemo(() => {
    if (submitted) return 'Thank you!';
    if (loading) return 'Sending...';
    return 'Submit';
  }, [loading, submitted]);

  return (
    <>
      <PageSEO title={getPageTitle('Contact')} description={siteMetadata.description} />

      <div className="my-8 grid grid-cols-1 gap-1 bg-white md:grid-cols-2 md:gap-2">
        <div className="col-span-1 p-6">
          <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-gray-100">
            Reach out to our team.
          </h1>
          <p className="text-left text-sm font-normal text-gray-700 dark:text-gray-200">
            We’re always looking to understand how we can help our customers make the best use of
            Exafunction.
          </p>
        </div>
        <div className="col-span-1">
          <div className="rounded-md bg-white p-10 text-left shadow-2xl">
            <h2 className="text-2xl leading-7 text-gray-900 dark:text-gray-100">Contact Us</h2>

            <form onSubmit={handleSubmit}>
              <TextInput label="Name" onValueChange={setName} value={name} id="name" required />
              <TextInput
                label="Work Email"
                onValueChange={setEmail}
                value={email}
                id="email"
                required
                type="email"
              />
              <TextInput
                label="Organization"
                onValueChange={setOrganization}
                value={organization}
                id="organization"
              />
              <TextInput
                label="Job Title"
                onValueChange={setJobTitle}
                value={jobTitle}
                id="jobTitle"
              />
              <TextInput
                label="Phone Number"
                onValueChange={setPhoneNumber}
                value={phoneNumber}
                id="phoneNumber"
                type="tel"
              />

              <div className="my-6">
                <label className="mb-1 block text-sm text-gray-700">
                  Tell us a bit about your use case
                </label>
                <textarea
                  className="focus:shadow-outline w-full appearance-none rounded border py-2 px-3 leading-tight text-gray-700 shadow focus:outline-none"
                  id="details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Use case"
                />
              </div>

              <div className="h-2 w-full" />
              <ActionButton disabled={submitted || !canSubmit} type="submit">
                {submitButtonText}
              </ActionButton>
            </form>
            {error && (
              <div className="w-72 pt-2 text-sm text-red-500 dark:text-red-400 sm:w-96">
                {error.message}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
